<script setup lang="ts">
import type {ComparisonBlock} from '~/types/legacy'
import type {Populated} from '~/types/populated'

const props = defineProps<{
  comparison?: Populated<ComparisonBlock> | null
}>()
</script>

<template>
  <Container v-if="comparison" class="screen-content flex flex-col py-12 text-center md:py-24">
    <Heading as="h2" class="mb-4 text-sm text-main-600 md:text-base" size="caption">
      {{ comparison.eyebrow }}
    </Heading>

    <div class="mb-8 flex flex-col items-center justify-center px-4 md:mb-16">
      <Heading as="h2" size="large" class="mb-4 text-2xl md:text-4xl">
        {{ $t('healthcare.comparison_section.title_part1') }}
        <span class="text-main-600">
          {{ $t('healthcare.comparison_section.title_vs') }}
        </span>
        {{ $t('healthcare.comparison_section.title_part2') }}
      </Heading>

      <Heading as="h2" size="large" class="mb-4 text-xl md:mb-6 md:text-2xl">
        {{ $t('healthcare.comparison_section.sub_title') }}
      </Heading>

      <Paragraph class="w-full px-2 text-base leading-relaxed text-gray-600 md:w-3/4 md:text-lg lg:w-2/3">
        {{ comparison.body }}
      </Paragraph>
    </div>

    <div class="mx-auto w-full px-4">
      <div class="hidden md:block">
        <div class="grid grid-cols-3">
          <div class="p-6"></div>
          <div class="p-6 text-left">
            <h3 class="font-semibold text-lg">
              {{ comparison.leftTitle }}
            </h3>

            <div
              class="mt-4 h-[2px] w-full bg-gradient-to-r from-main-400 via-gray-100 via-green-200 to-green-300"
            ></div>
          </div>
          <div class="p-6 text-left">
            <h3 class="font-semibold text-lg">
              {{ comparison.rightTitle }}
            </h3>

            <div
              class="mt-4 h-[2px] w-full bg-gradient-to-r from-main-400 via-gray-100 via-green-200 to-green-300"
            ></div>
          </div>
        </div>

        <div class="grid grid-cols-3">
          <div class="p-6">
            <div class="flex items-center">
              <div class="mr-3 flex h-8 w-8 items-center justify-center rounded-full"></div>
              <h4 class="text-lg font-medium">Pros</h4>
            </div>
          </div>

          <div class="p-6">
            <ul class="space-y-3">
              <li
                v-for="(pro, index) in comparison.leftPros"
                :key="index"
                class="flex items-start items-center text-left"
              >
                <Icon
                  class="h-6 w-6 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                  name="material-symbols:check-rounded"
                />
                <span class="ml-3 text-gray-700">{{ pro.item }}</span>
              </li>
            </ul>
            <div class="mt-4 block h-[1px] w-full bg-gray-300"></div>
          </div>

          <div class="p-6">
            <ul class="space-y-3">
              <li v-for="(pro, index) in comparison.rightPros" :key="index" class="flex items-start">
                <Icon
                  class="h-6 w-6 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                  name="material-symbols:check-rounded"
                />
                <span class="ml-2 text-gray-700">{{ pro.item }}</span>
              </li>
            </ul>
          </div>
        </div>

        <div class="grid grid-cols-3">
          <div class="p-6">
            <div class="flex items-center">
              <div class="mr-3 flex h-8 w-8 items-center justify-center rounded-full"></div>
              <h4 class="text-lg font-medium">Cons</h4>
            </div>
          </div>

          <div class="p-6">
            <ul class="space-y-3">
              <li v-for="(con, index) in comparison.leftCons" :key="index" class="flex items-start">
                <Icon
                  class="h-6 w-6 flex-shrink-0 rounded-full bg-red-200 p-1 text-red-800"
                  name="material-symbols-light:close-rounded"
                />
                <span class="ml-2 text-gray-700">{{ con.item }}</span>
              </li>
            </ul>
          </div>

          <div class="p-6">
            <ul class="space-y-3">
              <li v-for="(con, index) in comparison.rightCons" :key="index" class="flex items-start">
                <Icon
                  class="h-6 w-6 flex-shrink-0 rounded-full bg-red-200 p-1 text-red-800"
                  name="material-symbols-light:close-rounded"
                />
                <span class="ml-2 text-gray-700">{{ con.item }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="md:hidden">
        <div class="space-y-8">
          <div class="rounded-lg border border-gray-200 bg-white p-6">
            <h3 class="font-semibold mb-6 text-center text-xl">
              {{ comparison.leftTitle }}
            </h3>

            <div class="mb-6">
              <h4 class="mb-4 flex items-center text-lg font-medium">
                <span class="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-green-200">
                  <Icon class="h-4 w-4 text-green-800" name="material-symbols:check-rounded" />
                </span>
                Pros
              </h4>
              <ul class="space-y-3">
                <li v-for="(pro, index) in comparison.leftPros" :key="index" class="flex items-start text-left">
                  <Icon
                    class="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                    name="material-symbols:check-rounded"
                  />
                  <span class="ml-3 text-sm text-gray-700">{{ pro.item }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 class="mb-4 flex items-center text-lg font-medium">
                <span class="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-red-200">
                  <Icon class="h-4 w-4 text-red-800" name="material-symbols-light:close-rounded" />
                </span>
                Cons
              </h4>
              <ul class="space-y-3">
                <li v-for="(con, index) in comparison.leftCons" :key="index" class="flex items-start">
                  <Icon
                    class="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-red-200 p-1 text-red-800"
                    name="material-symbols-light:close-rounded"
                  />
                  <span class="ml-3 text-sm text-gray-700">{{ con.item }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="rounded-lg border border-gray-200 bg-white p-6">
            <h3 class="font-semibold mb-6 text-center text-xl">
              {{ comparison.rightTitle }}
            </h3>

            <div class="mb-6">
              <h4 class="mb-4 flex items-center text-lg font-medium">
                <span class="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-green-200">
                  <Icon class="h-4 w-4 text-green-800" name="material-symbols:check-rounded" />
                </span>
                Pros
              </h4>
              <ul class="space-y-3">
                <li v-for="(pro, index) in comparison.rightPros" :key="index" class="flex items-start">
                  <Icon
                    class="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-green-200 p-1 text-green-800"
                    name="material-symbols:check-rounded"
                  />
                  <span class="ml-3 text-sm text-gray-700">{{ pro.item }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 class="mb-4 flex items-center text-lg font-medium">
                <span class="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-red-200">
                  <Icon class="h-4 w-4 text-red-800" name="material-symbols-light:close-rounded" />
                </span>
                Cons
              </h4>
              <ul class="space-y-3">
                <li v-for="(con, index) in comparison.rightCons" :key="index" class="flex items-start">
                  <Icon
                    class="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-red-200 p-1 text-red-800"
                    name="material-symbols-light:close-rounded"
                  />
                  <span class="ml-3 text-sm text-gray-700">{{ con.item }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>
